import { validateReadyToPresent, type Screen } from '@floorpoll/shared';

/**
 * Per-screen pre-flight for the running order.
 *
 * Derived from `validateReadyToPresent` rather than re-stating its rules, so a
 * mark in the rail can never disagree with the gate the presenter actually
 * hits. That gate is deliberately stricter than saving: an empty screen is a
 * legal draft, so a screen can be saveable and still be marked unready here.
 */

export type ScreenState = 'ready' | 'empty' | 'unfinished';

export interface ScreenReadiness {
  readonly state: ScreenState;
  /** Plain-words line under the slug, e.g. "3 questions" or "Needs a question". */
  readonly detail: string;
}

const plural = (count: number, noun: string) => `${count} ${noun}${count === 1 ? '' : 's'}`;

export function readScreens(screens: Screen[]): ScreenReadiness[] {
  // One pass over the whole poll, bucketed by the leading `screens[N]` segment.
  // Issues with no screen index — "add at least one screen" — belong to the
  // poll rather than to a row, and fall out here.
  const blocking = screens.map(() => 0);
  for (const issue of validateReadyToPresent(screens)) {
    const index = Number(/^screens\[(\d+)\]/.exec(issue.path)?.[1]);
    if (Number.isInteger(index) && index < blocking.length)
      blocking[index] = (blocking[index] ?? 0) + 1;
  }

  return screens.map((screen, index) => {
    const questions = plural(screen.questions.length, 'question');
    if (screen.questions.length === 0) return { state: 'empty', detail: 'Needs a question' };
    if (blocking[index]) return { state: 'unfinished', detail: `${questions} · unfinished` };
    return { state: 'ready', detail: questions };
  });
}

/** The slug a cue is known by: its first question, or nothing yet. */
export function slugFor(screen: Screen): string | null {
  const text = screen.questions.find((question) => question.text.trim())?.text.trim();
  return text ?? null;
}

export function countQuestions(screens: Screen[]): number {
  return screens.reduce((total, screen) => total + screen.questions.length, 0);
}
